import React, { createContext, useCallback, useContext, useRef, useState } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  resolutionHint?: string;
  duration?: number; // ms, 0 = no auto-dismiss
}

interface NotificationContextValue {
  toasts: Toast[];
  toast: (t: Omit<Toast, 'id'>) => void;
  dismiss: (id: string) => void;
  success: (title: string, message?: string) => void;
  error: (title: string, message?: string, resolutionHint?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const toast = useCallback((t: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const duration = t.duration ?? (t.type === 'error' ? 8000 : 5000);

    setToasts(prev => {
      const next = [...prev, { ...t, id }];
      return next.slice(-5); // keep max 5 toasts
    });

    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timers.current.set(id, timer);
    }
  }, [dismiss]);

  const success = useCallback((title: string, message?: string) =>
    toast({ type: 'success', title, message }), [toast]);

  const error = useCallback((title: string, message?: string, resolutionHint?: string) =>
    toast({ type: 'error', title, message, resolutionHint, duration: 0 }), [toast]);

  const warning = useCallback((title: string, message?: string) =>
    toast({ type: 'warning', title, message }), [toast]);

  const info = useCallback((title: string, message?: string) =>
    toast({ type: 'info', title, message }), [toast]);

  return (
    <NotificationContext.Provider value={{ toasts, toast, dismiss, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotification must be used inside NotificationProvider');
  return ctx;
}

// ── Toast Container UI ──────────────────────────────────────────────────────

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
};

const COLORS: Record<ToastType, string> = {
  success: '#10b981',
  error:   '#ef4444',
  warning: '#f59e0b',
  info:    '#6366f1',
};

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
      maxWidth: 400,
      width: '100%',
    }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          background: 'var(--surface-2, #1e1e2e)',
          border: `1px solid ${COLORS[t.type]}44`,
          borderLeft: `4px solid ${COLORS[t.type]}`,
          borderRadius: 10,
          padding: '12px 14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          animation: 'toast-in 0.25s ease',
          position: 'relative',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            {/* Icon */}
            <div style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: `${COLORS[t.type]}22`,
              color: COLORS[t.type],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 700,
              flexShrink: 0,
              marginTop: 1,
            }}>
              {ICONS[t.type]}
            </div>

            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontWeight: 600,
                fontSize: 13,
                color: 'var(--text-1, #e2e8f0)',
                marginBottom: t.message || t.resolutionHint ? 4 : 0,
              }}>
                {t.title}
              </div>
              {t.message && (
                <div style={{ fontSize: 12, color: 'var(--text-2, #94a3b8)', lineHeight: 1.4 }}>
                  {t.message}
                </div>
              )}
              {t.resolutionHint && (
                <div style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: COLORS[t.type],
                  background: `${COLORS[t.type]}11`,
                  borderRadius: 6,
                  padding: '4px 8px',
                  lineHeight: 1.4,
                }}>
                  💡 {t.resolutionHint}
                </div>
              )}
            </div>

            {/* Close */}
            <button
              onClick={() => onDismiss(t.id)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-2, #94a3b8)',
                fontSize: 16,
                padding: 2,
                lineHeight: 1,
                flexShrink: 0,
              }}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      ))}
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateX(24px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
